import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Search, Ticket, User, Plus, MapPin, ArrowRight, Star, Users2, Briefcase } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";
import { resolveUploadUrl } from "@/lib/api";

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const CATEGORY_LABELS = { sedan: "Sedán", suv: "SUV", van: "Van" };

const ClientSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");

  const load = async (filterCity) => {
    setLoading(true);
    try {
      const { data } = await clientApi.listRoutes(filterCity ? { city: filterCity } : {});
      setRoutes(data);
    } catch {
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="client-search">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Buscar traslado</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Encuentra tu ruta ejecutiva</h1>

        <div className="mt-8 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6 lg:p-7">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 bg-[#0D0E10] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-4 py-3">
              <MapPin size={14} className="text-[#C9A227]" />
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Buscar por ciudad (ej. Miraflores)"
                className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
                data-testid="search-city"
              />
            </div>
            <button
              onClick={() => load(city)}
              className="px-6 py-3 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg flex items-center justify-center gap-2"
              data-testid="search-submit"
            >
              Buscar <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <div className="mt-10 flex items-baseline justify-between">
          <h2 className="font-display font-bold text-lg uppercase tracking-wide">
            Resultados <span className="text-[#A0A0A5]">({routes.length})</span>
          </h2>
        </div>

        {loading && <p className="mt-5 text-xs text-[#A0A0A5] font-body">Cargando…</p>}
        {!loading && routes.length === 0 && <p className="mt-5 text-xs text-[#A0A0A5] font-body">No hay rutas disponibles.</p>}

        <div className="mt-5 grid grid-cols-1 gap-3">
          {routes.map((r) => (
            <div key={r.id} className="bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/50 rounded-xl p-5 lg:p-6 transition-all" data-testid={`result-card-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-semibold text-sm">
                      {r.direction === "aeropuerto_ciudad" ? `${r.airport_name} → ${r.city}` : `${r.city} → ${r.airport_name}`}
                    </p>
                    {r.driver_rating_average != null && (
                      <span className="text-[10px] flex items-center gap-0.5 text-[#C9A227]">
                        <Star size={10} fill="currentColor" /> {r.driver_rating_average.toFixed(1)} ({r.driver_rating_count})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#A0A0A5] mt-1 flex items-center gap-3">
                    {r.vehicle && (
                      <>
                        {r.vehicle.brand} {r.vehicle.model} · {CATEGORY_LABELS[r.vehicle.category] || r.vehicle.category}
                      </>
                    )}
                    <span className="flex items-center gap-1">
                      <Briefcase size={10} /> {r.estimated_duration_minutes} min · {r.estimated_distance_km} km
                    </span>
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    {r.private_enabled && (
                      <span className="px-2 py-0.5 border border-[#2E2F33] rounded-full text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display">
                        Privado · S/ {r.private_price}
                      </span>
                    )}
                    {r.shared_enabled && (
                      <span className="px-2 py-0.5 border border-[#C9A227]/40 rounded-full text-[10px] uppercase tracking-wider text-[#C9A227] font-display flex items-center gap-1">
                        <Users2 size={10} /> Compartido · S/ {r.price_per_seat}/asiento
                      </span>
                    )}
                  </div>
                </div>
                {r.vehicle?.photo_url && (
                  <img src={resolveUploadUrl(r.vehicle.photo_url)} alt="Vehículo" className="w-full md:w-24 h-16 object-cover rounded-lg" />
                )}
                <button
                  onClick={() => navigate(`/cliente/reservar?routeId=${r.id}`)}
                  className="px-5 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg shrink-0"
                  data-testid={`select-route-${r.id}`}
                >
                  Reservar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PanelShell>
  );
};

export default ClientSearch;
